import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import handleRequest from '../utils/handleRequest';
import { SET_USERS } from '../actions/types';
import { GlobalContext } from '../Store';
import {useSnackbar} from "notistack";

const UsersContext = React.createContext();

const UsersProvider = (props) => {
  const { store, dispatch } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();

  const state = { users: store.users || [] };

  const errorNotification = (message) => enqueueSnackbar(message, {
    variant: 'error',
    anchorOrigin: { horizontal: 'right', vertical: 'bottom' }
  });

  const getUsers = useCallback(async () => {
    const res = await handleRequest({
      method: 'get',
      url: '/users',
    });

    if (res.status === 200) {
      dispatch({
        type: SET_USERS,
        payload: { users: res.result.users },
      });
    } else {
      // error fetching users
    }
  }, [dispatch]);

  const getUserById = useCallback(async (id) => {
    const user = state.users.find((user) => user._id === id )

    if (user) {
      return user;
    } else {
      // error user not exist
    }
  }, [state.users]);

  const getRoles = useCallback(async () => {
    const res = await handleRequest({
      method: 'get',
      url: '/users/roles',
    });

    if (res.status === 200) {
      return res.result.roles;
    } else {
      console.log('failed to retrieve roles. ', res.message);
    }
    return [];
  }, []);

  const createUser = useCallback(async (data, onSuccess) => {
    const res = await handleRequest({
      method: 'post',
      url: '/users',
      data,
    });

    if (res.status === 200) {
      const users = [...state.users, res.result.user];
      dispatch({
        type: SET_USERS,
        payload: { users },
      });
      typeof onSuccess === 'function' && onSuccess();
    } else {
      res.message?.message && errorNotification(res.message?.message);
      throw {key: res.message?.context?.key};
    }
  }, [state.users, dispatch]);

  const updateUser = useCallback(async (id, data, onSuccess) => {
    const res = await handleRequest({
      method: 'put',
      url: `/users/${id}`,
      data,
    });

    if (res.status === 200) {
      const users = state.users.map((user) => {
        return user._id === id ? res.result.user : user;
      });
      dispatch({
        type: SET_USERS,
        payload: { users },
      });
      typeof onSuccess === 'function' && onSuccess();
    } else {
      res.message?.message && errorNotification(res.message?.message);
      throw {key: res.message?.context?.key};
    }
  }, [state.users, dispatch]);

  const toggleStatus = useCallback(
    async (user) => {
      const status = user.status === 'active' ? 'inactive' : 'active';
      updateUser(user._id, { status }, ()=>{});
    },
    [updateUser]
  );

  const actions = {
    getUsers,
    getUserById,
    getRoles,
    toggleStatus,
    createUser,
    updateUser,
  };

  return (
    <UsersContext.Provider value={{ state, actions }}>
      {props.children}
    </UsersContext.Provider>
  );
};

UsersContext.propTypes = {
  children: PropTypes.element,
};

export { UsersProvider, UsersContext };
